/**
 * Stratalogy — Meta Pixel Helper
 *
 * Thin wrapper around the global `fbq()` function injected by the Meta Pixel
 * snippet in public/index.html.  Every call is guarded so the app never
 * throws if the pixel was blocked by an ad-blocker or failed to load.
 *
 * Standard events tracked:
 *   PageView          — auto-fired by the base snippet on every page load
 *   CompleteRegistration — fired after a successful /auth/register call
 *   Lead              — fired when onboarding is completed (account type set)
 *   Subscribe         — fired when Stripe checkout returns billing=success
 *   ViewContent       — fired on the /pricing page to measure ad→pricing intent
 *
 * Event deduplication:
 *   Each event includes an `event_id` (UUID) that is returned to the caller.
 *   The caller passes this ID to the backend API, which forwards it to the
 *   Conversions API.  Meta uses the matching `event_id` to deduplicate the
 *   browser pixel event and the server-side CAPI event.
 *
 * Click ID (fbc) capture:
 *   When a user clicks a Meta ad, the landing URL contains ?fbclid=xxx.
 *   This module captures that parameter on first load, stores it as the _fbc
 *   cookie in the format Meta expects (fb.1.{timestamp}.{fbclid}).
 *   The frontend reads the cookie via getMetaParams() and sends it in the
 *   API request body — no reliance on HTTP cookies reaching the backend.
 *
 * Browser ID (fbp):
 *   The Meta Pixel automatically sets an _fbp cookie. getMetaParams() reads
 *   it from JavaScript and includes it in the API request body.
 *
 * Advanced Matching:
 *   After login or registration, call setAdvancedMatching(email) to re-init
 *   the pixel with the user's hashed email.  This allows Meta to match
 *   pixel-side events to Facebook users even when _fbp is cleared.
 */

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Generate a UUID-v4 event ID for deduplication with the Conversions API. */
function eventId() {
  // crypto.randomUUID() is available in all modern browsers
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/** Safe wrapper — swallows errors if fbq is not loaded (ad-blocker, etc.) */
function safeFbq(...args) {
  try {
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      window.fbq(...args);
    }
  } catch {
    // Silently ignore — pixel tracking must never break the app
  }
}

// ── Cookie helpers ──────────────────────────────────────────────────────────

/** Read a cookie by name. Returns the value or null. */
function getCookie(name) {
  try {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
  } catch {
    return null;
  }
}

/** Set a first-party cookie. Default expiry: 90 days (Meta recommendation). */
function setCookie(name, value, days = 90) {
  try {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    // SameSite=Lax allows the cookie to be sent on top-level navigations
    // from Meta ad clicks. Path=/ ensures it's available across all routes.
    document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires};path=/;SameSite=Lax`;
  } catch {
    // Silently ignore — cookie write failure must never break the app
  }
}

// ── Click ID (fbc) capture ──────────────────────────────────────────────────

/**
 * Capture the fbclid parameter from the URL and store it as the _fbc cookie.
 *
 * Meta's expected _fbc format: fb.1.{creation_time_ms}.{fbclid}
 * See: https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/fbp-and-fbc
 *
 * Call this once on app mount (e.g., in App.js or index.js).
 * If _fbc already exists and there's no new fbclid in the URL, it's left alone.
 */
export function captureFbclid() {
  try {
    if (typeof window === 'undefined') return;

    const params = new URLSearchParams(window.location.search);
    const fbclid = params.get('fbclid');

    if (fbclid) {
      // Build the _fbc value in Meta's required format
      const fbc = `fb.1.${Date.now()}.${fbclid}`;
      setCookie('_fbc', fbc, 90);
    }
  } catch {
    // Silently ignore — must never break the app
  }
}

// ── Meta params for API requests ────────────────────────────────────────────

/**
 * Read Meta tracking cookies from JavaScript and return them as an object
 * suitable for including in API request bodies.
 *
 * This bypasses the problem of HTTP cookies not reaching the backend through
 * CDN/proxy layers.  The frontend reads the cookies directly and sends the
 * values in the JSON body.
 *
 * @returns {{ meta_fbc: string|null, meta_fbp: string|null }}
 */
export function getMetaCookies() {
  return {
    meta_fbc: getCookie('_fbc') || null,
    meta_fbp: getCookie('_fbp') || null,
  };
}

// ── Advanced Matching ───────────────────────────────────────────────────────

/**
 * Re-initialize the pixel with Advanced Matching user data.
 *
 * Call this after login or registration when you know the user's email.
 * Meta will hash the email client-side and use it to match pixel events
 * to Facebook users — even when _fbp is cleared by Safari ITP or ad-blockers.
 *
 * @param {string} email — the user's email address (unhashed; fbq hashes it)
 */
export function setAdvancedMatching(email) {
  if (!email) return;
  try {
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      // Re-init with user data — Meta's recommended approach for SPAs
      window.fbq('init', '1675013800356375', { em: email.toLowerCase().trim() });
    }
  } catch {
    // Silently ignore
  }
}

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Track a successful registration.
 * Call immediately after the /auth/register API responds 200.
 * @returns {string} event_id — pass this to the backend CAPI call
 */
export function trackCompleteRegistration() {
  const eid = eventId();
  safeFbq('track', 'CompleteRegistration', {}, { eventID: eid });
  return eid;
}

/**
 * Track a lead (onboarding completed, account type selected).
 * Call after the /auth/account-type API responds 200.
 * @param {string} accountType — homeowner | contractor | developer | commercial
 * @returns {string} event_id
 */
export function trackLead(accountType) {
  const eid = eventId();
  safeFbq('track', 'Lead', { content_name: accountType }, { eventID: eid });
  return eid;
}

/**
 * Track a new subscription (Stripe checkout success).
 * Call when the user returns from Stripe with billing=success.
 * @param {string} plan — homeowner | contractor | developer | commercial
 * @param {number} value — dollar amount of the plan
 * @returns {string} event_id
 */
export function trackSubscribe(plan, value) {
  const eid = eventId();
  safeFbq(
    'track',
    'Subscribe',
    {
      value: value || 0,
      currency: 'USD',
      predicted_ltv: value || 0,
      content_name: plan,
    },
    { eventID: eid },
  );
  return eid;
}

/**
 * Track pricing page view.
 * Call when the PricingPage component mounts (or the Settings page pricing
 * section scrolls into view).
 * @returns {string} event_id
 */
export function trackViewContent() {
  const eid = eventId();
  safeFbq(
    'track',
    'ViewContent',
    { content_name: 'Pricing Page', content_category: 'pricing' },
    { eventID: eid },
  );
  return eid;
}

/**
 * Track the start of a free trial.
 * Call after registration when the 7-day trial begins.
 * @returns {string} event_id
 */
export function trackStartTrial() {
  const eid = eventId();
  safeFbq(
    'track',
    'StartTrial',
    { value: 0, currency: 'USD', predicted_ltv: 39 },
    { eventID: eid },
  );
  return eid;
}
